// First-touch attribution capture.
//
// Populates a session-scoped record of UTM params, HTTP referrer and the
// landing pathname the *first* time a visitor arrives during a browser
// session. Subsequent client-side navigations do not overwrite the record —
// so if someone lands via `?utm_source=google` and then clicks around the
// site before submitting the contact form, we still know they came from
// Google. Cleared when the tab is closed.

const KEY = "lutrom.attribution.v1";

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"];

const isBrowser = () => typeof window !== "undefined";

const readExisting = () => {
  if (!isBrowser()) return null;
  try {
    const raw = window.sessionStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return typeof parsed === "object" && parsed !== null ? parsed : null;
  } catch {
    return null;
  }
};

const write = (record) => {
  if (!isBrowser()) return;
  try {
    window.sessionStorage.setItem(KEY, JSON.stringify(record));
  } catch {
    /* sessionStorage disabled — silently no-op */
  }
};

/**
 * Called once on app boot. If the current URL has UTM parameters, or if this
 * is the first navigation in the session, capture what we can. Never
 * overwrites an existing first-touch record.
 */
export const captureAttribution = () => {
  if (!isBrowser()) return;
  if (readExisting()) return; // first-touch — do not overwrite

  const params = new URLSearchParams(window.location.search);
  const record = {};
  for (const k of UTM_KEYS) {
    const v = params.get(k);
    if (v) record[k] = v.slice(0, 200);
  }
  // Referrer + landing page are always captured on the first pageview, even
  // if there are no UTM params (referrer alone is useful attribution).
  if (document.referrer) record.referrer = document.referrer.slice(0, 500);
  record.landing_page = (window.location.pathname + window.location.search).slice(0, 500);

  // Only persist if we actually captured something interesting. We always
  // have a landing_page, but recording that alone for every user is noisy;
  // only persist when we have UTM data or a non-empty referrer.
  const hasSignal =
    UTM_KEYS.some((k) => record[k]) || record.referrer;
  if (!hasSignal) return;

  write(record);
};

/**
 * Returns the current session's attribution record shaped for the
 * contact-submission payload. Returns `{}` if nothing was captured.
 */
export const getAttributionPayload = () => {
  const rec = readExisting();
  if (!rec) return {};
  const out = {};
  for (const k of [...UTM_KEYS, "referrer", "landing_page"]) {
    if (rec[k]) out[k] = rec[k];
  }
  return out;
};
